import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'fr' | 'en' | 'pt';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.about': 'À propos',
    'nav.africaSurfTour': 'Surf Tour',
    'nav.federations': 'Fédérations',
    'nav.talents': 'Talents',
    'nav.news': 'Actualités',
    'nav.gallery': 'Galerie',
    'nav.partners': 'Partenaires',
    'nav.contact': 'Contact',
    'nav.learnmore': 'En savoirs plus',
    'nav.event': 'Notre prochain évenement va bientôt commencer, clique sur le bouton pour en savoir plus',
    'nav.surfer': 'surfeurs',
    
    // Home Page
    'home.hero.title1': 'Ride the',
    'home.hero.title2': 'African Wave',
    'home.hero.subtitle': 'L\'Afrique unie sur la vague. Découvrez la passion, les talents et l\'esprit communautaire qui définissent le surf africain.',
    'home.hero.cta1': 'Explorer la Tournée',
    'home.hero.cta2': 'Voir la Vidéo',
    'home.stats.countries': 'Pays membres',
    'home.stats.competitions': 'Compétitions',
    'home.stats.athletes': 'Athlètes',
    'home.stats.experience': 'Années d\'expérience',
    'home.tour.title': 'Africa Surf Tour',
    'home.tour.subtitle': 'Suivez nos athlètes à travers les plus beaux spots de surf du continent africain',
    'home.tour.cta': 'Découvrir Toutes les Étapes',
    'home.mission.title': 'Notre Mission',
    'home.mission.description': 'Depuis 2017, l\'ASCsurf unit les nations africaines autour de la passion du surf. Nous développons les talents locaux, organisons des compétitions de niveau mondial et promouvons l\'Afrique sur la scène internationale du surf.',
    'home.mission.point1': 'Développement des talents africains',
    'home.mission.point2': 'Compétitions professionnelles',
    'home.mission.point3': 'Représentation internationale',
    'home.mission.cta': 'En savoir plus',
    'home.cta.title': 'Rejoignez le Mouvement',
    'home.cta.subtitle': 'Faites partie de la communauté du surf africain. Suivez nos actualités, découvrez nos talents et vivez la passion des vagues africaines.',
    'home.cta.contact': 'Nous Contacter',
    'home.cta.news': 'Suivre l\'Actualité',
    
    // About Page
    'about.hero.title': 'À propos de l\'ASCsurf',
    'about.hero.subtitle': 'Fédérer les nations africaines autour du surf depuis 2017',
    'about.vision.title': 'Notre Vision',
    'about.vision.p1': 'L\'ASCsurf (Confédération Africaine de Surf) a été fondée en 2017 à Rabat avec une mission claire : unir les nations africaines autour de la passion du surf et développer ce sport sur tout le continent.',
    'about.vision.p2': 'Sous la présidence d\'Oumar Seye, nous œuvrons pour faire rayonner les talents africains sur la scène mondiale et utiliser le surf comme un vecteur de développement économique, social et écologique.',
    'about.vision.quote': 'Le surf africain n\'est pas seulement un sport, c\'est une culture, une connexion profonde avec nos océans et un moyen d\'unir nos peuples.',
    'about.vision.author': 'Oumar Seye, Président ASCsurf',
    'about.vision.founded': 'Fondée en',
    'about.vision.based': 'Basée à',
    'about.objectives.title': 'Nos Objectifs',
    'about.objectives.subtitle': 'Quatre piliers fondamentaux guident notre action pour le développement du surf africain',
    'about.objectives.talent': 'Développer les talents locaux',
    'about.objectives.talent.desc': 'Former et accompagner la nouvelle génération de surfeurs africains vers l\'excellence.',
    'about.objectives.competitions': 'Organiser des compétitions',
    'about.objectives.competitions.desc': 'Créer des événements de niveau international pour promouvoir le surf africain.',
    'about.objectives.representation': 'Représentation mondiale',
    'about.objectives.representation.desc': 'Porter la voix de l\'Afrique dans les instances internationales comme l\'ISA et les JO.',
    'about.objectives.economic': 'Levier économique',
    'about.objectives.economic.desc': 'Utiliser le surf comme moteur de développement économique et écologique.',
    'about.history.title': 'Notre Histoire',
    'about.history.subtitle': 'Les étapes clés de notre développement',
    'about.leadership.title': 'Leadership',
    'about.leadership.subtitle': 'Une équipe passionnée au service du surf africain',
    'about.leadership.bio': 'Visionnaire et passionné, Oumar Seye dirige l\'ASCsurf avec une ambition : faire de l\'Afrique une puissance mondiale du surf tout en préservant l\'authenticité et les valeurs de notre continent.',
    "about.documents.title": "Documents & Rapports officiels",
    "about.documents.subtitle": "Consultez et téléchargez les documents clés de l'African Surf Confederation",

    // Africa Surf Tour
    'tour.hero.title': 'Africa Surf Tour',
    'tour.hero.subtitle': 'Le circuit professionnel qui unit les meilleurs surfeurs du continent africain',
    'tour.hero.steps': 'Etapes',
    'tour.featured.title': 'Événement à la Une',
    'tour.map.title': 'Carte du Circuit',
    'tour.map.subtitle': 'Suivez le parcours épique à travers les plus beaux spots de surf d\'Afrique',
    'tour.champions.title': 'Champions',
    'tour.champions.subtitle': 'Découvrez les talents qui ont marqué l\'Africa Surf Tour',
    'tour.media.title': 'Galerie Média',
    'tour.media.subtitle': 'Revivez les moments forts du tour en images et vidéos',
    'tour.next.title': 'Africa Surf Tour 2025',
    'tour.next.subtitle': 'Préparez-vous pour une nouvelle édition encore plus spectaculaire. Inscriptions ouvertes pour les surfeurs professionnels africains.',
    'tour.next.info': 'Informations Inscription',
    'tour.next.calendar': 'Calendrier 2025',
    'tour.stops.senegal': 'Sénégal',
    'tour.stops.cotedivoire': 'Côte d\'Ivoire',
    'tour.stops.liberia': 'Liberia',
    'tour.stops.morocco': 'Maroc',
    'tour.stops.ghana': 'Ghana',
    'tour.stops.angola': 'Angola',
    'tour.stats.stages': 'Étapes',
    'tour.stats.participants': 'Participants',
    'tour.stats.prizePool': 'Prize Pool',
    'tour.highlights': 'Points forts',
    'tour.winners': 'Vainqueurs',
    'tour.men': 'Hommes',
    'tour.women': 'Femmes',
    
    // Federations
    'federations.hero.title': 'Fédérations Membres',
    'federations.hero.subtitle': 'Pays Membres',
    'federations.leadership.title': 'Bureau Executif',
    'federations.leadership.subtitle': 'Une équipe passionnée au service du surf africain',
    'federations.honor.title': 'Présidents d\'Honneur',
    'federations.honor.subtitle': 'Des figures emblématiques soutenant notre mission',
    'federations.join.title': 'Rejoindre l\'ASCsurf',
    'federations.join.subtitle': 'Votre pays n\'est pas encore membre ? Découvrez comment rejoindre la grande famille du surf africain et bénéficier de notre réseau continental.',
    'federations.join.criteria': 'Critères d\'Adhésion',
    'federations.join.contact': 'Nous Contacter',
    'federations.filter.all': 'Toutes les Régions',
    'federations.filter.north': 'Afrique du Nord',
    'federations.filter.west': 'Afrique de l\'Ouest',
    'federations.filter.south': 'Afrique Australe',
    'federations.filter.east': 'Afrique de l\'Est',
    'federations.filter.central': 'Afrique Centrale',
    'federations.stats.countries': 'Pays Membres',
    'federations.stats.surfers': 'Surfeurs Licenciés',
    'federations.stats.averageYear': 'Année Moyenne',
    'federations.president': 'Président',
    'federations.founded': 'Fondée',
    'federations.members': 'Membres',
    'federations.spots': 'Spots Principaux',
    'federations.achievements': 'Réalisations',
    'federations.contact': 'Contact',
    'federations.website': 'Site Web',
    
    // Talents
    'talents.hero.title': 'Talents Africains',
    'talents.hero.subtitle': 'Découvrez les surfeurs qui portent les couleurs de l\'Afrique vers l\'excellence mondiale',
    'talents.hero.athletes': 'Athlètes',
    'talents.hero.country': 'Pays',
    'talents.programs.title': 'Programmes de Développement',
    'talents.programs.subtitle': 'Des initiatives concrètes pour former les champions de demain',
    'talents.stories.title': 'Success Stories',
    'talents.stories.subtitle': 'De l\'Afrique aux plus grandes compétitions mondiales',
    'about.objectives.one.title': 'Développer les talents locaux',
    'about.objectives.one.subtitle': 'Former et accompagner la nouvelle génération de surfeurs africains vers l\'excellence.',
    'about.objectives.two.title': 'Organiser des compétitions',
    'about.objectives.two.subtitle': 'Créer des événements de niveau international pour promouvoir le surf africain.',
    'about.objectives.three.title': 'Représentation mondiale',
    'about.objectives.three.subtitle': 'Porter la voix de l\'Afrique dans les instances internationales comme l\'ISA et les JO.',
    'about.objectives.for.title': 'Levier économique',
    'about.objectives.for.subtitle': 'Utiliser le surf comme moteur de développement économique et écologique.',
    'talents.become.title': 'Devenir un Talent ASCsurf',
    'talents.become.subtitle': 'Vous êtes un jeune surfeur prometteur ? Découvrez nos programmes de formation et rejoignez l\'élite du surf africain.',
    'talents.become.apply': 'Candidater',
    'talents.become.programs': 'Nos Programmes',
    'talents.filter.all': 'Tous les Athlètes',
    'talents.filter.professional': 'Professionnels',
    'talents.filter.emerging': 'Talents Émergents',
    'talents.stats.athletes': 'Athlètes',
    'talents.stats.countries': 'Pays',
    'talents.stats.training': 'En Formation',
    'talents.category.professional': 'PRO',
    'talents.category.emerging': 'TALENT',
    'talents.programs.youth': 'Programme Jeunes Talents',
    'talents.programs.academy': 'Académie Professionnelle',
    'talents.programs.women': 'Surf Féminin',
    'talents.viewProfile': 'Voir le profil',
    'talents.competitions': 'Compét.',
    'talents.victories': 'Victoires',
    'talents.podiums': 'Podiums',
    'talents.accomplishments': 'Accomplissements',
    
    // News
    'news.hero.title': 'Actualités',
    'news.hero.subtitle': 'Suivez toute l\'actualité du surf africain et de l\'ASCsurf',
    'news.featured.title': 'À la Une',
    'news.featured.subtitle': 'Les actualités les plus importantes',
    'news.podcast.title': 'Voix de la Vague',
    'news.podcast.subtitle': 'Le podcast mensuel de l\'ASCsurf',
    'news.newsletter.title': 'Newsletter ASCsurf',
    'news.newsletter.subtitle': 'Restez informé de toute l\'actualité du surf africain',
    'news.newsletter.placeholder': 'Votre email',
    'news.newsletter.subscribe': 'S\'abonner',
    'news.social.title': 'Suivez-nous',
    'news.events.title': 'Événements à Venir',
    'news.categories.all': 'Toutes les Actualités',
    'news.categories.competitions': 'Compétitions',
    'news.categories.athletes': 'Athlètes',
    'news.categories.development': 'Développement',
    'news.categories.partnerships': 'Partenariats',
    'news.categories.community': 'Communauté',
    'news.categories.environment': 'Environnement',
    'news.readMore': 'Lire plus',
    'news.readTime': 'min de lecture',
    'news.author': 'Auteur',
    'news.publishedOn': 'Publié le',
    'news.tags': 'Tags',
    'news.share': 'Partager',
    'news.relatedArticles': 'Articles Similaires',
    'news.backToNews': 'Retour aux actualités',
    'news.article.notFound': 'Article non trouvé',
    'news.article.backToNews': 'Retour aux actualités',
    'news.article.readTime': 'min de lecture',
    'news.article.tags': 'Tags',
    'news.article.share': 'Partager cet article',
    'news.article.relatedArticles': 'Articles Similaires',
    'news.article.newsletter.title': 'Ne manquez aucune actualité',
    'news.article.newsletter.subtitle': 'Inscrivez-vous à notre newsletter pour recevoir les dernières nouvelles du surf africain.',
    
    // Gallery
    'gallery.hero.title': 'Galerie',
    'gallery.hero.subtitle': 'Découvrez l\'Afrique du surf en images et vidéos',
    'gallery.photos.title': 'Photos',
    'gallery.videos.title': 'Vidéos',
    'gallery.collections.title': 'Collections',
    'gallery.latest.title': 'Derniers Ajouts',
    'gallery.latest.subtitle': 'Les dernières photos et vidéos ajoutées à notre galerie',
    'gallery.filter.all': 'Tout',
    'gallery.filter.photos': 'Photos',
    'gallery.filter.videos': 'Vidéos',
    'gallery.collection.all': 'Toutes les Collections',
    'gallery.collection.competitions': 'Compétitions',
    'gallery.collection.spots': 'Spots de Surf',
    'gallery.collection.athletes': 'Athlètes',
    'gallery.collection.documentaries': 'Documentaires',
    'gallery.collection.training': 'Formation',
    'gallery.collection.events': 'Événements',
    'gallery.stats.galleries': 'Galeries',
    'gallery.stats.photos': 'Photos',
    'gallery.stats.videos': 'Vidéos',
    'gallery.view': 'Voir',
    'gallery.download': 'Télécharger',
    'gallery.close': 'Fermer',
    'gallery.previous': 'Précédent',
    'gallery.next': 'Suivant',
    'gallery.viewMode.grid': 'Grille',
    'gallery.viewMode.list': 'Liste',

    // Live 
    'live.event.title': 'Événement ASC en Direct',
    'live.event.subtitle': 'Regardez les meilleurs surfeurs mondiaux en direct !',
    
    // Partners
    'partners.hero.title': 'Nos Partenaires',
    'partners.hero.subtitle': 'Ensemble, nous construisons l\'avenir du surf africain',
    'partners.main.title': 'Partenaires Principaux',
    'partners.main.subtitle': 'Des marques de référence qui partagent notre vision du surf africain',
    'partners.support.title': 'Partenaires Soutien',
    'partners.support.subtitle': 'Un écosystème complet de marques engagées',
    'partners.benefits.title': 'Pourquoi Nous Rejoindre ?',
    'partners.benefits.subtitle': 'Les avantages d\'un partenariat avec l\'ASCsurf',
    'partners.levels.title': 'Niveaux de Partenariat',
    'partners.levels.subtitle': 'Des solutions adaptées à tous les budgets et objectifs',
    'partners.resources.title': 'Ressources Partenaires',
    'partners.resources.subtitle': 'Tout ce dont vous avez besoin pour démarrer',
    'partners.join.title': 'Rejoignez l\'Aventure',
    'partners.join.subtitle': 'Devenez partenaire de l\'ASCsurf et participez au développement du surf africain sur la scène mondiale.',
    'partners.join.meeting': 'Demander un RDV',
    'partners.stats.partners': 'Partenaires',
    'partners.stats.countries': 'Pays Couverts',
    'partners.stats.audience': 'Audience',
    'partners.benefits.visibility': 'Visibilité Internationale',
    'partners.benefits.visibility.desc': 'Exposition sur tous nos événements et plateformes digitales à travers 15 pays africains.',
    'partners.benefits.audience': 'Audience Engagée',
    'partners.benefits.audience.desc': 'Accès à une communauté passionnée de 300+ athlètes et milliers de fans.',
    'partners.benefits.premium': 'Association Premium',
    'partners.benefits.premium.desc': 'Alignement avec les valeurs d\'excellence, d\'authenticité et de développement durable.',
    'partners.benefits.custom': 'Partenariat Sur-Mesure',
    'partners.benefits.custom.desc': 'Solutions personnalisées selon vos objectifs marketing et RSE.',
    'partners.levels.title.main': 'Partenaire Titre',
    'partners.levels.title.official': 'Partenaire Officiel',
    'partners.levels.title.support': 'Partenaire Soutien',
    'partners.levels.price.season': 'par saison',
    'partners.levels.popular': 'Populaire',
    'partners.resources.sponsorship': 'Dossier de Sponsoring',
    'partners.resources.sponsorship.desc': 'Document complet avec toutes les opportunités de partenariat et les tarifs.',
    'partners.resources.media': 'Kit Média',
    'partners.resources.media.desc': 'Logos, photos haute résolution et éléments graphiques pour vos campagnes.',
    'partners.resources.contact': 'Contact Dédié',
    'partners.resources.contact.desc': 'Échangez directement avec notre équipe partenariats pour un projet sur-mesure.',
    'partners.resources.download': 'Télécharger PDF',
    'partners.resources.access': 'Accéder au Kit',
    'partners.resources.write': 'Nous Écrire',
    'partners.contact.us': 'Nous Contacter',
    'partners.since': 'Depuis',
    'partners.category.title': 'Titre',
    'partners.category.equipment': 'Équipement',
    'partners.category.institutional': 'Institutionnel',
    'partners.category.official': 'Officiel',
    'partners.category.support': 'Soutien',
    'partners.learnMore': 'En savoir plus',
    'partners.viewProjects': 'Voir les projets',
    
    // Contact
    'contact.hero.title': 'Contactez-Nous',
    'contact.hero.subtitle': 'Nous sommes là pour répondre à toutes vos questions sur le surf africain',
    'contact.form.title': 'Envoyez-nous un Message',
    'contact.form.name': 'Nom complet',
    'contact.form.email': 'Email',
    'contact.form.type': 'Type de demande',
    'contact.form.subject': 'Sujet',
    'contact.form.message': 'Message',
    'contact.form.send': 'Envoyer le Message',
    'contact.form.placeholder.name': 'Votre nom',
    'contact.form.placeholder.email': 'votre@email.com',
    'contact.form.placeholder.subject': 'Sujet de votre message',
    'contact.form.placeholder.message': 'Décrivez votre demande en détail...',
    'contact.form.types.general': 'Demande générale',
    'contact.form.types.partnership': 'Partenariat',
    'contact.form.types.competition': 'Compétitions',
    'contact.form.types.media': 'Médias',
    'contact.form.types.athlete': 'Inscription athlète',
    'contact.form.types.federation': 'Adhésion fédération',
    'contact.info.title': 'Informations de Contact',
    'contact.info.address': 'Adresse',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Téléphone',
    'contact.info.hours': 'Horaires',
    'contact.departments.title': 'Contacts par Département',
    'contact.departments.subtitle': 'Contactez directement le bon interlocuteur',
    'contact.departments.management': 'Direction Générale',
    'contact.departments.one': 'Équipe Marketing',
    'contact.departments.two': 'Équipe Com-Technique',
    'contact.departments.competitions': 'Compétitions',
    'contact.departments.partnerships': 'Partenariats',
    'contact.departments.media': 'Médias & Communication',
    'contact.location.title': 'Notre Localisation',
    'contact.location.subtitle': 'Siège social à Dakar, Sénégal',
    'contact.location.find': 'Comment nous Trouver',
    'contact.location.address': 'Adresse Complète',
    'contact.location.transport': 'Transports',
    'contact.location.landmarks': 'Points de Repère',
    'contact.location.taxi': 'Taxi: Demander "Surfer Paradise" corniche des almadies',
    'contact.location.repere': 'Juste après la station de lavage sur la corniche des almadies',
    'contact.newsletter.title': 'Restez Connecté',
    'contact.newsletter.subtitle': 'Inscrivez-vous à notre newsletter pour recevoir toutes les actualités du surf africain directement dans votre boîte mail.',
    'contact.social.instagram': 'Instagram',
    'contact.social.youtube': 'YouTube',
    'contact.social.facebook': 'Facebook',
    'contact.social.followers': 'followers',
    'contact.hours.weekdays': 'Lun - Ven: 9h - 18h',
    'contact.hours.saturday': 'Sam: 9h - 13h',
    
    // Admin
    'admin.login.title': 'Connexion Administration',
    'admin.login.subtitle': 'Accédez au panneau d\'administration ASCsurf',
    'admin.login.email': 'Email',
    'admin.login.password': 'Mot de passe',
    'admin.login.submit': 'Se connecter',
    'admin.login.forgot': 'Mot de passe oublié ?',
    'admin.dashboard.title': 'Tableau de Bord',
    'admin.dashboard.welcome': 'Bienvenue dans l\'administration ASCsurf',
    'admin.menu.dashboard': 'Tableau de Bord',
    'admin.menu.news': 'Actualités',
    'admin.menu.athletes': 'Athlètes',
    'admin.menu.federations': 'Fédérations',
    'admin.menu.partners': 'Partenaires',
    'admin.menu.gallery': 'Galerie',
    'admin.menu.sliders': 'Sliders',
    'admin.menu.users': 'Utilisateurs',
    'admin.menu.settings': 'Paramètres',
    'admin.menu.logout': 'Déconnexion',
    'admin.stats.news': 'Actualités',
    'admin.stats.athletes': 'Athlètes',
    'admin.stats.federations': 'Fédérations',
    'admin.stats.partners': 'Partenaires',
    'admin.stats.galleries': 'Galeries',
    'admin.stats.sliders': 'Sliders',
    'admin.stats.users': 'Utilisateurs',
    'admin.stats.messages': 'Messages',
    'admin.activity.recent': 'Activité Récente',
    'admin.activity.newArticle': 'Nouvel article publié',
    'admin.activity.athleteAdded': 'Athlète ajouté',
    'admin.activity.galleryUpdated': 'Galerie mise à jour',
    'admin.quickActions.title': 'Actions Rapides',
    'admin.quickActions.newArticle': 'Nouvel Article',
    'admin.quickActions.newAthlete': 'Nouvel Athlète',
    'admin.quickActions.newGallery': 'Nouvelle Galerie',
    'admin.quickActions.newSlider': 'Nouveau Slider',
    'admin.table.search': 'Rechercher...',
    'admin.table.filter': 'Filtrer',
    'admin.table.actions': 'Actions',
    'admin.table.edit': 'Modifier',
    'admin.table.view': 'Voir',
    'admin.table.delete': 'Supprimer',
    'admin.table.noResults': 'Aucun résultat trouvé',
    'admin.modal.add': 'Ajouter',
    'admin.modal.edit': 'Modifier',
    'admin.modal.save': 'Enregistrer',
    'admin.modal.cancel': 'Annuler',
    'admin.modal.close': 'Fermer',
    'admin.settings.general': 'Paramètres généraux',
    'admin.settings.siteName': 'Nom du site',
    'admin.settings.contactEmail': 'Email de contact',
    'admin.settings.defaultLanguage': 'Langue par défaut',
    'admin.settings.save': 'Sauvegarder',
    'admin.news.title': 'Gestion des Actualités',
    'admin.news.add': 'Nouvel Article',
    'admin.athletes.title': 'Gestion des Athlètes',
    'admin.athletes.add': 'Nouvel Athlète',
    'admin.federations.title': 'Gestion des Fédérations',
    'admin.federations.add': 'Nouvelle Fédération',
    'admin.partners.title': 'Gestion des Partenaires',
    'admin.partners.add': 'Nouveau Partenaire',
    'admin.gallery.title': 'Gestion de la Galerie',
    'admin.gallery.add': 'Nouvelle Galerie',
    'admin.sliders.title': 'Gestion des Sliders',
    'admin.sliders.add': 'Nouveau Slider',
    'admin.users.title': 'Gestion des Utilisateurs',
    'admin.users.add': 'Nouvel Utilisateur',
    
    // Footer
    'footer.description': 'Fédérer les nations africaines autour du surf, développer les talents locaux et promouvoir l\'Afrique sur la scène mondiale du surf.',
    'footer.navigation': 'Navigation',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2025 ASCsurf - Confédération Africaine de Surf. Tous droits réservés.',
    
    // Common
    'common.readMore': 'Lire plus',
    'common.learnMore': 'En savoir plus',
    'common.seeAll': 'Voir tout',
    'common.loading': 'Chargement...',
    'common.error': 'Une erreur est survenue',
    'common.success': 'Succès',
    'common.email': 'Email',
    'common.phone': 'Téléphone',
    'common.address': 'Adresse',
    'common.website': 'Site Web',
    'common.founded': 'Fondée',
    'common.members': 'Membres',
    'common.participants': 'Participants',
    'common.competitions': 'Compétitions',
    'common.victories': 'Victoires',
    'common.podiums': 'Podiums',
    'common.achievements': 'Réalisations',
    'common.sponsors': 'Sponsors',
    'common.country': 'Pays',
    'common.age': 'Âge',
    'common.specialty': 'Spécialité',
    'common.ranking': 'Classement',
    'common.send': 'Envoyer',
    'common.subscribe': 'S\'abonner',
    'common.followUs': 'Suivez-nous',
    'common.contactUs': 'Nous Contacter',
    'common.download': 'Télécharger',
    'common.viewProfile': 'Voir le profil',
    'common.upcomingEvents': 'Événements à Venir',
    'common.featuredNews': 'À la Une',
    'common.allNews': 'Toutes les Actualités',
    'common.categories': 'Catégories',
    'common.tags': 'Tags',
    'common.author': 'Auteur',
    'common.date': 'Date',
    'common.readTime': 'Temps de lecture',
    'common.location': 'Lieu',
    'common.prizePool': 'Prize Pool',
    'common.winners': 'Vainqueurs',
    'common.highlights': 'Points forts',
    'common.men': 'Hommes',
    'common.women': 'Femmes',
    'common.professional': 'Professionnel',
    'common.emerging': 'Émergent',
    'common.close': 'Fermer',
    'common.open': 'Ouvrir',
    'common.show': 'Afficher',
    'common.hide': 'Masquer',
    'common.expand': 'Développer',
    'common.collapse': 'Réduire',
    'common.previous': 'Précédent',
    'common.next': 'Suivant',
    'common.first': 'Premier',
    'common.last': 'Dernier',
    'common.page': 'Page',
    'common.of': 'de',
    'common.total': 'Total',
    'common.results': 'résultats',
    'common.noResults': 'Aucun résultat',
    'common.selectAll': 'Tout sélectionner',
    'common.deselectAll': 'Tout désélectionner',
    'common.selected': 'sélectionné(s)',
    'common.required': 'Obligatoire',
    'common.optional': 'Optionnel',
    'common.choose': 'Choisir',
    'common.upload': 'Téléverser',
    'common.browse': 'Parcourir',
    'common.clear': 'Effacer',
    'common.reset': 'Réinitialiser',
    'common.apply': 'Appliquer',
    'common.submit': 'Soumettre',
    'common.continue': 'Continuer',
    'common.back': 'Retour',
    'common.finish': 'Terminer',
    'common.skip': 'Ignorer',
    'common.retry': 'Réessayer',
    'common.refresh': 'Actualiser',
    'common.reload': 'Recharger',
    'common.update': 'Mettre à jour',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
    'common.confirm': 'Confirmer',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.sort': 'Trier',
    'common.actions': 'Actions',
    'common.status': 'Statut',
    'common.active': 'Actif',
    'common.inactive': 'Inactif',
    'common.published': 'Publié',
    'common.draft': 'Brouillon',
    'common.title': 'Titre',
    'common.description': 'Description',
    'common.content': 'Contenu',
    'common.image': 'Image',
    'common.video': 'Vidéo',
    'common.gallery': 'Galerie',
    'common.collection': 'Collection',
    'common.slider': 'Slider',
    'common.slide': 'Diapositive',
    'common.edit': 'Modifier',
    'common.delete': 'Supprimer',
    'common.add': 'Ajouter',
    'common.view': 'Voir'
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.africaSurfTour': 'Africa Surf Tour',
    'nav.federations': 'Federations',
    'nav.talents': 'Talents',
    'nav.news': 'News',
    'nav.gallery': 'Gallery',
    'nav.partners': 'Partners',
    'nav.contact': 'Contact',
    'nav.admin': 'Administration',
    'nav.learnmore': 'Learn more',
    'nav.event': 'Our next event is coming soon, click the button to find out more',
    'nav.surfer': 'surfers',
    
    // Home Page
    'home.hero.title1': 'Ride the',
    'home.hero.title2': 'African Wave',
    'home.hero.subtitle': 'Africa united on the wave. Discover the passion, talents and community spirit that define African surfing.',
    'home.hero.cta1': 'Explore the Tour',
    'home.hero.cta2': 'Watch Video',
    'home.stats.countries': 'Member Countries',
    'home.stats.competitions': 'Competitions',
    'home.stats.athletes': 'Athletes',
    'home.stats.experience': 'Years of Experience',
    'home.tour.title': 'Africa Surf Tour',
    'home.tour.subtitle': 'Follow our athletes through the most beautiful surf spots on the African continent',
    'home.tour.cta': 'Discover All Stages',
    'home.mission.title': 'Our Mission',
    'home.mission.description': 'Since 2017, ASCsurf has united African nations around the passion for surfing. We develop local talents, organize world-class competitions and promote Africa on the international surfing scene.',
    'home.mission.point1': 'African talent development',
    'home.mission.point2': 'Professional competitions',
    'home.mission.point3': 'International representation',
    'home.mission.cta': 'Learn more',
    'home.cta.title': 'Join the Movement',
    'home.cta.subtitle': 'Be part of the African surfing community. Follow our news, discover our talents and experience the passion of African waves.',
    'home.cta.contact': 'Contact Us',
    'home.cta.news': 'Follow News',
    
    // About Page
    'about.hero.title': 'About ASCsurf',
    'about.hero.subtitle': 'Uniting African nations around surfing since 2017',
    'about.vision.title': 'Our Vision',
    'about.vision.p1': 'ASCsurf (African Surfing Confederation) was founded in 2017 in Rabat with a clear mission: to unite African nations around the passion for surfing and develop this sport across the continent.',
    'about.vision.p2': 'Under the presidency of Oumar Seye, we work to make African talents shine on the world stage and use surfing as a vector for economic, social and ecological development.',
    'about.vision.quote': 'African surfing is not just a sport, it\'s a culture, a deep connection with our oceans and a way to unite our peoples.',
    'about.vision.author': 'Oumar Seye, ASCsurf President',
    'about.vision.founded': 'Founded in',
    'about.vision.based': 'Based in',
    'about.objectives.title': 'Our Objectives',
    'about.objectives.subtitle': 'Four fundamental pillars guide our action for the development of African surfing',
    'about.objectives.talent': 'Develop local talents',
    'about.objectives.talent.desc': 'Train and support the new generation of African surfers towards excellence.',
    'about.objectives.competitions': 'Organize competitions',
    'about.objectives.competitions.desc': 'Create international-level events to promote African surfing.',
    'about.objectives.representation': 'Global representation',
    'about.objectives.representation.desc': 'Carry Africa\'s voice in international bodies like ISA and the Olympics.',
    'about.objectives.economic': 'Economic leverage',
    'about.objectives.economic.desc': 'Use surfing as a driver for economic and ecological development.',
    'about.history.title': 'Our History',
    'about.history.subtitle': 'Key milestones in our development',
    'about.leadership.title': 'Leadership',
    'about.leadership.subtitle': 'A passionate team serving African surfing',
    'about.leadership.bio': 'Visionary and passionate, Oumar Seye leads ASCsurf with one ambition: to make Africa a world surfing power while preserving the authenticity and values of our continent.',
    "about.documents.title": "Official Documents & Reports",
    "about.documents.subtitle": "View and download the key documents of the African Surf Confederation",
    // Africa Surf Tour
    'tour.hero.title': 'Africa Surf Tour',
    'tour.hero.subtitle': 'The professional circuit that unites the best surfers of the African continent',
    'tour.map.title': 'Circuit Map',
    'tour.featured.title': 'Featured Event',
    'tour.hero.steps': 'Steps',
    'tour.map.subtitle': 'Follow the epic journey through Africa\'s most beautiful surf spots',
    'tour.champions.title': 'Champions',
    'tour.champions.subtitle': 'Discover the talents who marked the Africa Surf Tour',
    'tour.media.title': 'Media Gallery',
    'tour.media.subtitle': 'Relive the tour\'s highlights in images and videos',
    'tour.next.title': 'Africa Surf Tour 2025',
    'tour.next.subtitle': 'Get ready for an even more spectacular new edition. Registration open for African professional surfers.',
    'tour.next.info': 'Registration Information',
    'tour.next.calendar': '2025 Calendar',
    'tour.stops.senegal': 'Senegal',
    'tour.stops.cotedivoire': 'Côte d\'Ivoire',
    'tour.stops.liberia': 'Liberia',
    'tour.stops.morocco': 'Morocco',
    'tour.stops.ghana': 'Ghana',
    'tour.stops.angola': 'Angola',
    'tour.stats.stages': 'Stages',
    'tour.stats.participants': 'Participants',
    'tour.stats.prizePool': 'Prize Pool',
    'tour.highlights': 'Highlights',
    'tour.winners': 'Winners',
    'tour.men': 'Men',
    'tour.women': 'Women',
    
    // Federations
    'federations.hero.title': 'Member Federations',
    'federations.hero.subtitle': 'Member Countries',
    'federations.leadership.title': 'Executive Office',
    'federations.leadership.subtitle': 'A passionate team serving African surfing',
    'federations.honor.title': 'Honorary Presidents',
    'federations.honor.subtitle': 'Emblematic figures supporting our mission',
    'federations.join.title': 'Join ASCsurf',
    'federations.join.subtitle': 'Your country is not yet a member? Discover how to join the great African surfing family and benefit from our continental network.',
    'federations.join.criteria': 'Membership Criteria',
    'federations.join.contact': 'Contact Us',
    'federations.filter.all': 'All Regions',
    'federations.filter.north': 'North Africa',
    'federations.filter.west': 'West Africa',
    'federations.filter.south': 'Southern Africa',
    'federations.filter.east': 'East Africa',
    'federations.filter.central': 'Central Africa',
    'federations.stats.countries': 'Member Countries',
    'federations.stats.surfers': 'Licensed Surfers',
    'federations.stats.averageYear': 'Average Year',
    'federations.president': 'President',
    'federations.founded': 'Founded',
    'federations.members': 'Members',
    'federations.spots': 'Main Spots',
    'federations.achievements': 'Achievements',
    'federations.contact': 'Contact',
    'federations.website': 'Website',

    // Live 
    'live.event.title': 'ASC Live Event',
    'live.event.subtitle': 'Watch the world’s best surfers compete live!',
    
    // Talents
    'talents.hero.title': 'African Talents',
    'talents.hero.subtitle': 'Discover the surfers who carry Africa\'s colors towards world excellence',
    'talents.hero.athletes': 'Athletes',
    'talents.hero.country': 'Country',
    'about.objectives.one.title': 'Developing local talent',
    'about.objectives.one.subtitle': 'Train and support the new generation of African surfers towards excellence.',
    'about.objectives.two.title': 'Organize competitions',
    'about.objectives.two.subtitle': 'Create international level events to promote African surfing.',
    'about.objectives.three.title': 'Global Representation',
    'about.objectives.three.subtitle': 'Carrying Africa voice in international bodies such as the ISA and the Olympic Games.',
    'about.objectives.for.title': 'Economic lever',
    'about.objectives.for.subtitle': 'Using surfing as a driver of economic and ecological development.',
    'talents.programs.title': 'Development Programs',
    'talents.programs.subtitle': 'Concrete initiatives to train tomorrow\'s champions',
    'talents.stories.title': 'Success Stories',
    'talents.stories.subtitle': 'From Africa to the world\'s biggest competitions',
    'talents.become.title': 'Become an ASCsurf Talent',
    'talents.become.subtitle': 'Are you a promising young surfer? Discover our training programs and join the African surfing elite.',
    'talents.become.apply': 'Apply',
    'talents.become.programs': 'Our Programs',
    'talents.filter.all': 'All Athletes',
    'talents.filter.professional': 'Professionals',
    'talents.filter.emerging': 'Emerging Talents',
    'talents.stats.athletes': 'Athletes',
    'talents.stats.countries': 'Countries',
    'talents.stats.training': 'In Training',
    'talents.category.professional': 'PRO',
    'talents.category.emerging': 'TALENT',
    'talents.programs.youth': 'Youth Talent Program',
    'talents.programs.academy': 'Professional Academy',
    'talents.programs.women': 'Women\'s Surfing',
    'talents.viewProfile': 'View profile',
    'talents.competitions': 'Comps.',
    'talents.victories': 'Victories',
    'talents.podiums': 'Podiums',
    'talents.accomplishments': 'Accomplishments',
    
    // News
    'news.hero.title': 'News',
    'news.hero.subtitle': 'Follow all the news from African surfing and ASCsurf',
    'news.featured.title': 'Featured',
    'news.featured.subtitle': 'The most important news',
    'news.podcast.title': 'Voice of the Wave',
    'news.podcast.subtitle': 'ASCsurf\'s monthly podcast',
    'news.newsletter.title': 'ASCsurf Newsletter',
    'news.newsletter.subtitle': 'Stay informed about all African surfing news',
    'news.newsletter.placeholder': 'Your email',
    'news.newsletter.subscribe': 'Subscribe',
    'news.social.title': 'Follow Us',
    'news.events.title': 'Upcoming Events',
    'news.categories.all': 'All News',
    'news.categories.competitions': 'Competitions',
    'news.categories.athletes': 'Athletes',
    'news.categories.development': 'Development',
    'news.categories.partnerships': 'Partnerships',
    'news.categories.community': 'Community',
    'news.categories.environment': 'Environment',
    'news.readMore': 'Read more',
    'news.readTime': 'min read',
    'news.author': 'Author',
    'news.publishedOn': 'Published on',
    'news.tags': 'Tags',
    'news.share': 'Share',
    'news.relatedArticles': 'Related Articles',
    'news.backToNews': 'Back to news',
    'news.article.notFound': 'Article not found',
    'news.article.backToNews': 'Back to news',
    'news.article.readTime': 'min read',
    'news.article.tags': 'Tags',
    'news.article.share': 'Share this article',
    'news.article.relatedArticles': 'Related Articles',
    'news.article.newsletter.title': 'Don\'t miss any news',
    'news.article.newsletter.subtitle': 'Subscribe to our newsletter to receive the latest African surfing news.',
    
    // Gallery
    'gallery.hero.title': 'Gallery',
    'gallery.hero.subtitle': 'Discover surfing Africa in images and videos',
    'gallery.photos.title': 'Photos',
    'gallery.videos.title': 'Videos',
    'gallery.collections.title': 'Collections',
    'gallery.latest.title': 'Latest Additions',
    'gallery.latest.subtitle': 'The latest photos and videos added to our gallery',
    'gallery.filter.all': 'All',
    'gallery.filter.photos': 'Photos',
    'gallery.filter.videos': 'Videos',
    'gallery.collection.all': 'All Collections',
    'gallery.collection.competitions': 'Competitions',
    'gallery.collection.spots': 'Surf Spots',
    'gallery.collection.athletes': 'Athletes',
    'gallery.collection.documentaries': 'Documentaries',
    'gallery.collection.training': 'Training',
    'gallery.collection.events': 'Events',
    'gallery.stats.galleries': 'Galleries',
    'gallery.stats.photos': 'Photos',
    'gallery.stats.videos': 'Videos',
    'gallery.view': 'View',
    'gallery.download': 'Download',
    'gallery.close': 'Close',
    'gallery.previous': 'Previous',
    'gallery.next': 'Next',
    'gallery.viewMode.grid': 'Grid',
    'gallery.viewMode.list': 'List',
    
    // Partners
    'partners.hero.title': 'Our Partners',
    'partners.hero.subtitle': 'Together, we build the future of African surfing',
    'partners.main.title': 'Main Partners',
    'partners.main.subtitle': 'Reference brands that share our vision of African surfing',
    'partners.support.title': 'Support Partners',
    'partners.support.subtitle': 'A complete ecosystem of committed brands',
    'partners.benefits.title': 'Why Join Us?',
    'partners.benefits.subtitle': 'The benefits of a partnership with ASCsurf',
    'partners.levels.title': 'Partnership Levels',
    'partners.levels.subtitle': 'Solutions adapted to all budgets and objectives',
    'partners.resources.title': 'Partner Resources',
    'partners.resources.subtitle': 'Everything you need to get started',
    'partners.join.title': 'Join the Adventure',
    'partners.join.subtitle': 'Become an ASCsurf partner and participate in the development of African surfing on the world stage.',
    'partners.join.meeting': 'Request a Meeting',
    'partners.stats.partners': 'Partners',
    'partners.stats.countries': 'Countries Covered',
    'partners.stats.audience': 'Audience',
    'partners.benefits.visibility': 'International Visibility',
    'partners.benefits.visibility.desc': 'Exposure on all our events and digital platforms across 15 African countries.',
    'partners.benefits.audience': 'Engaged Audience',
    'partners.benefits.audience.desc': 'Access to a passionate community of 300+ athletes and thousands of fans.',
    'partners.benefits.premium': 'Premium Association',
    'partners.benefits.premium.desc': 'Alignment with values of excellence, authenticity and sustainable development.',
    'partners.benefits.custom': 'Custom Partnership',
    'partners.benefits.custom.desc': 'Personalized solutions according to your marketing and CSR objectives.',
    'partners.levels.title.main': 'Title Partner',
    'partners.levels.title.official': 'Official Partner',
    'partners.levels.title.support': 'Support Partner',
    'partners.levels.price.season': 'per season',
    'partners.levels.popular': 'Popular',
    'partners.resources.sponsorship': 'Sponsorship Package',
    'partners.resources.sponsorship.desc': 'Complete document with all partnership opportunities and rates.',
    'partners.resources.media': 'Media Kit',
    'partners.resources.media.desc': 'Logos, high-resolution photos and graphic elements for your campaigns.',
    'partners.resources.contact': 'Dedicated Contact',
    'partners.resources.contact.desc': 'Exchange directly with our partnerships team for a custom project.',
    'partners.resources.download': 'Download PDF',
    'partners.resources.access': 'Access Kit',
    'partners.resources.write': 'Write to Us',
    'partners.contact.us': 'Contact Us',
    'partners.since': 'Since',
    'partners.category.title': 'Title',
    'partners.category.equipment': 'Equipment',
    'partners.category.institutional': 'Institutional',
    'partners.category.official': 'Official',
    'partners.category.support': 'Support',
    'partners.learnMore': 'Learn more',
    'partners.viewProjects': 'View projects',
    
    // Contact
    'contact.hero.title': 'Contact Us',
    'contact.hero.subtitle': 'We are here to answer all your questions about African surfing',
    'contact.form.title': 'Send us a Message',
    'contact.form.name': 'Full name',
    'contact.form.email': 'Email',
    'contact.form.type': 'Request type',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.send': 'Send Message',
    'contact.form.placeholder.name': 'Your name',
    'contact.form.placeholder.email': 'your@email.com',
    'contact.form.placeholder.subject': 'Subject of your message',
    'contact.form.placeholder.message': 'Describe your request in detail...',
    'contact.form.types.general': 'General inquiry',
    'contact.form.types.partnership': 'Partnership',
    'contact.form.types.competition': 'Competitions',
    'contact.form.types.media': 'Media',
    'contact.form.types.athlete': 'Athlete registration',
    'contact.form.types.federation': 'Federation membership',
    'contact.info.title': 'Contact Information',
    'contact.info.address': 'Address',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Phone',
    'contact.info.hours': 'Hours',
    'contact.departments.title': 'Contacts by Department',
    'contact.departments.subtitle': 'Contact the right person directly',
    'contact.departments.management': 'General Management',
    'contact.departments.one': 'Marketing Team',
    'contact.departments.two': 'Com-Technical Team',
    'contact.departments.competitions': 'Competitions',
    'contact.departments.partnerships': 'Partnerships',
    'contact.departments.media': 'Media & Communication',
    'contact.location.title': 'Our Location',
    'contact.location.subtitle': 'Headquarters in Dakar, Senegal',
    'contact.location.find': 'How to Find Us',
    'contact.location.address': 'Full Address',
    'contact.location.transport': 'Transportation',
    'contact.location.landmarks': 'Landmarks',
    'contact.location.taxi': 'Taxi: Ask for “Surfer Paradise” Corniche des Almadies',
    'contact.location.repere': 'Just after the car wash on the Corniche des Almadies',
    'contact.newsletter.title': 'Stay Connected',
    'contact.newsletter.subtitle': 'Subscribe to our newsletter to receive all African surfing news directly in your inbox.',
    'contact.social.instagram': 'Instagram',
    'contact.social.youtube': 'YouTube',
    'contact.social.facebook': 'Facebook',
    'contact.social.followers': 'followers',
    'contact.hours.weekdays': 'Mon - Fri: 9am - 6pm',
    'contact.hours.saturday': 'Sat: 9am - 1pm',
    
    // Admin
    'admin.login.title': 'Administration Login',
    'admin.login.subtitle': 'Access the ASCsurf administration panel',
    'admin.login.email': 'Email',
    'admin.login.password': 'Password',
    'admin.login.submit': 'Sign in',
    'admin.login.forgot': 'Forgot password?',
    'admin.dashboard.title': 'Dashboard',
    'admin.dashboard.welcome': 'Welcome to ASCsurf administration',
    'admin.menu.dashboard': 'Dashboard',
    'admin.menu.news': 'News',
    'admin.menu.athletes': 'Athletes',
    'admin.menu.federations': 'Federations',
    'admin.menu.partners': 'Partners',
    'admin.menu.gallery': 'Gallery',
    'admin.menu.sliders': 'Sliders',
    'admin.menu.users': 'Users',
    'admin.menu.settings': 'Settings',
    'admin.menu.logout': 'Logout',
    'admin.stats.news': 'News',
    'admin.stats.athletes': 'Athletes',
    'admin.stats.federations': 'Federations',
    'admin.stats.partners': 'Partners',
    'admin.stats.galleries': 'Galleries',
    'admin.stats.sliders': 'Sliders',
    'admin.stats.users': 'Users',
    'admin.stats.messages': 'Messages',
    'admin.activity.recent': 'Recent Activity',
    'admin.activity.newArticle': 'New article published',
    'admin.activity.athleteAdded': 'Athlete added',
    'admin.activity.galleryUpdated': 'Gallery updated',
    'admin.quickActions.title': 'Quick Actions',
    'admin.quickActions.newArticle': 'New Article',
    'admin.quickActions.newAthlete': 'New Athlete',
    'admin.quickActions.newGallery': 'New Gallery',
    'admin.quickActions.newSlider': 'New Slider',
    'admin.table.search': 'Search...',
    'admin.table.filter': 'Filter',
    'admin.table.actions': 'Actions',
    'admin.table.edit': 'Edit',
    'admin.table.view': 'View',
    'admin.table.delete': 'Delete',
    'admin.table.noResults': 'No results found',
    'admin.modal.add': 'Add',
    'admin.modal.edit': 'Edit',
    'admin.modal.save': 'Save',
    'admin.modal.cancel': 'Cancel',
    'admin.modal.close': 'Close',
    'admin.settings.general': 'General settings',
    'admin.settings.siteName': 'Site name',
    'admin.settings.contactEmail': 'Contact email',
    'admin.settings.defaultLanguage': 'Default language',
    'admin.settings.save': 'Save',
    'admin.news.title': 'News Management',
    'admin.news.add': 'New Article',
    'admin.athletes.title': 'Athletes Management',
    'admin.athletes.add': 'New Athlete',
    'admin.federations.title': 'Federations Management',
    'admin.federations.add': 'New Federation',
    'admin.partners.title': 'Partners Management',
    'admin.partners.add': 'New Partner',
    'admin.gallery.title': 'Gallery Management',
    'admin.gallery.add': 'New Gallery',
    'admin.sliders.title': 'Sliders Management',
    'admin.sliders.add': 'New Slider',
    'admin.users.title': 'Users Management',
    'admin.users.add': 'New User',
    
    // Footer
    'footer.description': 'Unite African nations around surfing, develop local talents and promote Africa on the world surfing scene.',
    'footer.navigation': 'Navigation',
    'footer.contact': 'Contact',
    'footer.copyright': '© 2025 ASCsurf - African Surfing Confederation. All rights reserved.',
    
    // Common
    'common.readMore': 'Read more',
    'common.learnMore': 'Learn more',
    'common.seeAll': 'See all',
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.success': 'Success',
    'common.email': 'Email',
    'common.phone': 'Phone',
    'common.address': 'Address',
    'common.website': 'Website',
    'common.founded': 'Founded',
    'common.members': 'Members',
    'common.participants': 'Participants',
    'common.competitions': 'Competitions',
    'common.victories': 'Victories',
    'common.podiums': 'Podiums',
    'common.achievements': 'Achievements',
    'common.sponsors': 'Sponsors',
    'common.country': 'Country',
    'common.age': 'Age',
    'common.specialty': 'Specialty',
    'common.ranking': 'Ranking',
    'common.send': 'Send',
    'common.subscribe': 'Subscribe',
    'common.followUs': 'Follow Us',
    'common.contactUs': 'Contact Us',
    'common.download': 'Download',
    'common.viewProfile': 'View profile',
    'common.upcomingEvents': 'Upcoming Events',
    'common.featuredNews': 'Featured',
    'common.allNews': 'All News',
    'common.categories': 'Categories',
    'common.tags': 'Tags',
    'common.author': 'Author',
    'common.date': 'Date',
    'common.readTime': 'Read time',
    'common.location': 'Location',
    'common.prizePool': 'Prize Pool',
    'common.winners': 'Winners',
    'common.highlights': 'Highlights',
    'common.men': 'Men',
    'common.women': 'Women',
    'common.professional': 'Professional',
    'common.emerging': 'Emerging',
    'common.close': 'Close',
    'common.open': 'Open',
    'common.show': 'Show',
    'common.hide': 'Hide',
    'common.expand': 'Expand',
    'common.collapse': 'Collapse',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'common.first': 'First',
    'common.last': 'Last',
    'common.page': 'Page',
    'common.of': 'of',
    'common.total': 'Total',
    'common.results': 'results',
    'common.noResults': 'No results',
    'common.selectAll': 'Select all',
    'common.deselectAll': 'Deselect all',
    'common.selected': 'selected',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.choose': 'Choose',
    'common.upload': 'Upload',
    'common.browse': 'Browse',
    'common.clear': 'Clear',
    'common.reset': 'Reset',
    'common.apply': 'Apply',
    'common.submit': 'Submit',
    'common.continue': 'Continue',
    'common.back': 'Back',
    'common.finish': 'Finish',
    'common.skip': 'Skip',
    'common.retry': 'Retry',
    'common.refresh': 'Refresh',
    'common.reload': 'Reload',
    'common.update': 'Update',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.published': 'Published',
    'common.draft': 'Draft',
    'common.title': 'Title',
    'common.description': 'Description',
    'common.content': 'Content',
    'common.image': 'Image',
    'common.video': 'Video',
    'common.gallery': 'Gallery',
    'common.collection': 'Collection',
    'common.slider': 'Slider',
    'common.slide': 'Slide',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.add': 'Add',
    'common.view': 'View'
  },
    pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.about': 'Sobre',
    'nav.africaSurfTour': 'Africa Surf Tour',
    'nav.federations': 'Federações',
    'nav.talents': 'Talentos',
    'nav.news': 'Notícias',
    'nav.gallery': 'Galeria',
    'nav.partners': 'Parceiros',
    'nav.contact': 'Contato',
    'nav.admin': 'Administração',
    'nav.learnmore': 'Saiba mais',
    'nav.event': 'Nosso próximo evento está chegando, clique no botão para saber mais',
    'nav.surfer': 'surfistas',
    
    // Home Page
    'home.hero.title1': 'Surfe a',
    'home.hero.title2': 'Onda Africana',
    'home.hero.subtitle': 'África unida na onda. Descubra a paixão, os talentos e o espírito comunitário que definem o surfe africano.',
    'home.hero.cta1': 'Explorar o Tour',
    'home.hero.cta2': 'Ver Vídeo',
    'home.stats.countries': 'Países Membros',
    'home.stats.competitions': 'Competições',
    'home.stats.athletes': 'Atletas',
    'home.stats.experience': 'Anos de Experiência',
    'home.tour.title': 'Africa Surf Tour',
    'home.tour.subtitle': 'Acompanhe nossos atletas pelos mais belos picos de surfe do continente africano',
    'home.tour.cta': 'Descubra Todas as Etapas',
    'home.mission.title': 'Nossa Missão',
    'home.mission.description': 'Desde 2017, a ASCsurf une nações africanas em torno da paixão pelo surfe. Desenvolvemos talentos locais, organizamos competições de nível mundial e promovemos a África na cena internacional do surfe.',
    'home.mission.point1': 'Desenvolvimento de talentos africanos',
    'home.mission.point2': 'Competições profissionais',
    'home.mission.point3': 'Representação internacional',
    'home.mission.cta': 'Saiba mais',
    'home.cta.title': 'Junte-se ao Movimento',
    'home.cta.subtitle': 'Faça parte da comunidade do surfe africano. Acompanhe nossas notícias, descubra nossos talentos e viva a paixão das ondas africanas.',
    'home.cta.contact': 'Contate-nos',
    'home.cta.news': 'Seguir Notícias',
    
    // About Page
    'about.hero.title': 'Sobre a ASCsurf',
    'about.hero.subtitle': 'Unindo nações africanas em torno do surfe desde 2017',
    'about.vision.title': 'Nossa Visão',
    'about.vision.p1': 'A ASCsurf (Confederação Africana de Surfe) foi fundada em 2017 em Rabat com uma missão clara: unir as nações africanas em torno da paixão pelo surfe e desenvolver este esporte em todo o continente.',
    'about.vision.p2': 'Sob a presidência de Oumar Seye, trabalhamos para fazer os talentos africanos brilharem no cenário mundial e usar o surfe como vetor de desenvolvimento econômico, social e ecológico.',
    'about.vision.quote': 'O surfe africano não é apenas um esporte, é uma cultura, uma conexão profunda com nossos oceanos e uma forma de unir nossos povos.',
    'about.vision.author': 'Oumar Seye, Presidente da ASCsurf',
    'about.vision.founded': 'Fundada em',
    'about.vision.based': 'Sediada em',
    'about.objectives.title': 'Nossos Objetivos',
    'about.objectives.subtitle': 'Quatro pilares fundamentais guiam nossa ação para o desenvolvimento do surfe africano',
    'about.objectives.talent': 'Desenvolver talentos locais',
    'about.objectives.talent.desc': 'Treinar e apoiar a nova geração de surfistas africanos rumo à excelência.',
    'about.objectives.competitions': 'Organizar competições',
    'about.objectives.competitions.desc': 'Criar eventos de nível internacional para promover o surfe africano.',
    'about.objectives.representation': 'Representação global',
    'about.objectives.representation.desc': 'Levar a voz da África a instituições internacionais como a ISA e as Olimpíadas.',
    'about.objectives.economic': 'Alavanca econômica',
    'about.objectives.economic.desc': 'Usar o surfe como motor de desenvolvimento econômico e ecológico.',
    'about.history.title': 'Nossa História',
    'about.history.subtitle': 'Marcos principais do nosso desenvolvimento',
    'about.leadership.title': 'Liderança',
    'about.leadership.subtitle': 'Uma equipe apaixonada a serviço do surfe africano',
    'about.leadership.bio': 'Visionário e apaixonado, Oumar Seye lidera a ASCsurf com uma ambição: fazer da África uma potência mundial do surfe, preservando a autenticidade e os valores do nosso continente.',
    "about.documents.title": "Documentos & Relatórios Oficiais",
    "about.documents.subtitle": "Consulte e descarregue os documentos principais da African Surf Confederation",
    // Africa Surf Tour
    'tour.hero.title': 'Africa Surf Tour',
    'tour.hero.subtitle': 'O circuito profissional que une os melhores surfistas do continente africano',
    'tour.hero.steps': 'Etapas',
    'tour.featured.title': 'Evento em Destaque',
    'tour.map.title': 'Mapa do Circuito',
    'tour.map.subtitle': 'Acompanhe a jornada épica pelos mais belos picos de surfe da África',
    'tour.champions.title': 'Campeões',
    'tour.champions.subtitle': 'Conheça os talentos que marcaram o Africa Surf Tour',
    'tour.media.title': 'Galeria de Mídia',
    'tour.media.subtitle': 'Reviva os momentos marcantes do tour em imagens e vídeos',
    'tour.next.title': 'Africa Surf Tour 2025',
    'tour.next.subtitle': 'Prepare-se para uma nova edição ainda mais espetacular. Inscrições abertas para surfistas profissionais africanos.',
    'tour.next.info': 'Informações de Inscrição',
    'tour.next.calendar': 'Calendário 2025',
    'tour.stops.senegal': 'Senegal',
    'tour.stops.cotedivoire': 'Costa do Marfim',
    'tour.stops.liberia': 'Libéria',
    'tour.stops.morocco': 'Marrocos',
    'tour.stops.ghana': 'Gana',
    'tour.stops.angola': 'Angola',
    'tour.stats.stages': 'Etapas',
    'tour.stats.participants': 'Participantes',
    'tour.stats.prizePool': 'Prêmio Total',
    'tour.highlights': 'Destaques',
    'tour.winners': 'Vencedores',
    'tour.men': 'Homens',
    'tour.women': 'Mulheres',
    
    // Federations
    'federations.hero.title': 'Federações Membros',
    'federations.hero.subtitle': 'Países Membros',
    'federations.leadership.title': 'Escritório Executivo',
    'federations.leadership.subtitle': 'Uma equipe apaixonada a serviço do surfe africano',
    'federations.honor.title': 'Presidentes Honorários',
    'federations.honor.subtitle': 'Figuras emblemáticas que apoiam nossa missão',
    'federations.join.title': 'Junte-se à ASCsurf',
    'federations.join.subtitle': 'Seu país ainda não é membro? Descubra como se juntar à grande família do surfe africano e se beneficiar da nossa rede continental.',
    'federations.join.criteria': 'Critérios de Adesão',
    'federations.join.contact': 'Contate-nos',
    'federations.filter.all': 'Todas as Regiões',
    'federations.filter.north': 'África do Norte',
    'federations.filter.west': 'África Ocidental',
    'federations.filter.south': 'África Austral',
    'federations.filter.east': 'África Oriental',
    'federations.filter.central': 'África Central',
    'federations.stats.countries': 'Países Membros',
    'federations.stats.surfers': 'Surfistas Licenciados',
    'federations.stats.averageYear': 'Ano Médio',
    'federations.president': 'Presidente',
    'federations.founded': 'Fundada',
    'federations.members': 'Membros',
    'federations.spots': 'Principais Picos',
    'federations.achievements': 'Conquistas',
    'federations.contact': 'Contato',
    'federations.website': 'Site',
    
    // Talents
    'talents.hero.title': 'Talentos Africanos',
    'talents.hero.subtitle': 'Conheça os surfistas que levam as cores da África rumo à excelência mundial',
    'talents.hero.athletes': 'Atletas',
    'talents.hero.country': 'País',
    'talents.programs.title': 'Programas de Desenvolvimento',
    'talents.programs.subtitle': 'Iniciativas concretas para formar os campeões de amanhã',
    'talents.stories.title': 'Histórias de Sucesso',
    'talents.stories.subtitle': 'Da África às maiores competições mundiais',
    'about.objectives.one.title': 'Desenvolver talentos locais',
    'about.objectives.one.subtitle': 'Treinar e apoiar a nova geração de surfistas africanos rumo à excelência.',
    'about.objectives.two.title': 'Organizar competições',
    'about.objectives.two.subtitle': 'Criar eventos de nível internacional para promover o surfe africano.',
    'about.objectives.three.title': 'Representação global',
    'about.objectives.three.subtitle': 'Levar a voz da África a instituições internacionais como a ISA e as Olimpíadas.',
    'about.objectives.for.title': 'Alavanca econômica',
    'about.objectives.for.subtitle': 'Usar o surfe como motor de desenvolvimento econômico e ecológico.',
    'talents.become.title': 'Torne-se um Talento ASCsurf',
    'talents.become.subtitle': 'Você é um jovem surfista promissor? Conheça nossos programas de treinamento e junte-se à elite do surfe africano.',
    'talents.become.apply': 'Inscrever-se',
    'talents.become.programs': 'Nossos Programas',
    'talents.filter.all': 'Todos os Atletas',
    'talents.filter.professional': 'Profissionais',
    'talents.filter.emerging': 'Talentos Emergentes',
    'talents.stats.athletes': 'Atletas',
    'talents.stats.countries': 'Países',
    'talents.stats.training': 'Em Treinamento',
    'talents.category.professional': 'PRO',
    'talents.category.emerging': 'TALENTO',
    'talents.programs.youth': 'Programa Jovens Talentos',
    'talents.programs.academy': 'Academia Profissional',
    'talents.programs.women': 'Surfe Feminino',
    'talents.viewProfile': 'Ver perfil',
    'talents.competitions': 'Comp.',
    'talents.victories': 'Vitórias',
    'talents.podiums': 'Pódios',
    'talents.accomplishments': 'Conquistas',
    
    // News
    'news.hero.title': 'Notícias',
    'news.hero.subtitle': 'Acompanhe todas as notícias do surfe africano e da ASCsurf',
    'news.featured.title': 'Em Destaque',
    'news.featured.subtitle': 'As notícias mais importantes',
    'news.podcast.title': 'Voz da Onda',
    'news.podcast.subtitle': 'O podcast mensal da ASCsurf',
    'news.newsletter.title': 'Newsletter ASCsurf',
    'news.newsletter.subtitle': 'Mantenha-se informado sobre todas as notícias do surfe africano',
    'news.newsletter.placeholder': 'Seu email',
    'news.newsletter.subscribe': 'Inscrever-se',
    'news.social.title': 'Siga-nos',
    'news.events.title': 'Eventos Futuros',
    'news.categories.all': 'Todas as Notícias',
    'news.categories.competitions': 'Competições',
    'news.categories.athletes': 'Atletas',
    'news.categories.development': 'Desenvolvimento',
    'news.categories.partnerships': 'Parcerias',
    'news.categories.community': 'Comunidade',
    'news.categories.environment': 'Meio Ambiente',
    'news.readMore': 'Leia mais',
    'news.readTime': 'min de leitura',
    'news.author': 'Autor',
    'news.publishedOn': 'Publicado em',
    'news.tags': 'Tags',
    'news.share': 'Compartilhar',
    'news.relatedArticles': 'Artigos Relacionados',
    'news.backToNews': 'Voltar às notícias',
    'news.article.notFound': 'Artigo não encontrado',
    'news.article.backToNews': 'Voltar às notícias',
    'news.article.readTime': 'min de leitura',
    'news.article.tags': 'Tags',
    'news.article.share': 'Compartilhar este artigo',
    'news.article.relatedArticles': 'Artigos Relacionados',
    'news.article.newsletter.title': 'Não perca nenhuma notícia',
    'news.article.newsletter.subtitle': 'Inscreva-se na nossa newsletter para receber as últimas notícias do surfe africano.',
    
    // Gallery
    'gallery.hero.title': 'Galeria',
    'gallery.hero.subtitle': 'Descubra o surfe africano em imagens e vídeos',
    'gallery.photos.title': 'Fotos',
    'gallery.videos.title': 'Vídeos',
    'gallery.collections.title': 'Coleções',
    'gallery.latest.title': 'Últimas Adições',
    'gallery.latest.subtitle': 'As últimas fotos e vídeos adicionados à nossa galeria',
    'gallery.filter.all': 'Tudo',
    'gallery.filter.photos': 'Fotos',
    'gallery.filter.videos': 'Vídeos',
    'gallery.collection.all': 'Todas as Coleções',
    'gallery.collection.competitions': 'Competições',
    'gallery.collection.spots': 'Picos de Surfe',
    'gallery.collection.athletes': 'Atletas',
    'gallery.collection.documentaries': 'Documentários',
    'gallery.collection.training': 'Treinamento',
    'gallery.collection.events': 'Eventos',
    'gallery.stats.galleries': 'Galerias',
    'gallery.stats.photos': 'Fotos',
    'gallery.stats.videos': 'Vídeos',
    'gallery.view': 'Ver',
    'gallery.download': 'Baixar',
    'gallery.close': 'Fechar',
    'gallery.previous': 'Anterior',
    'gallery.next': 'Próximo',
    'gallery.viewMode.grid': 'Grade',
    'gallery.viewMode.list': 'Lista',
    
    // Live 
    'live.event.title': 'Evento ASC ao Vivo',
    'live.event.subtitle': 'Assista aos melhores surfistas do mundo competindo ao vivo!',
    
    // Partners
    'partners.hero.title': 'Nossos Parceiros',
    'partners.hero.subtitle': 'Juntos, construímos o futuro do surfe africano',
    'partners.main.title': 'Parceiros Principais',
    'partners.main.subtitle': 'Marcas de referência que compartilham nossa visão do surfe africano',
    'partners.support.title': 'Parceiros de Apoio',
    'partners.support.subtitle': 'Um ecossistema completo de marcas engajadas',
    'partners.benefits.title': 'Por que se Juntar a Nós?',
    'partners.benefits.subtitle': 'Os benefícios de uma parceria com a ASCsurf',
    'partners.levels.title': 'Níveis de Parceria',
    'partners.levels.subtitle': 'Soluções adaptadas a todos os orçamentos e objetivos',
    'partners.resources.title': 'Recursos para Parceiros',
    'partners.resources.subtitle': 'Tudo o que você precisa para começar',
    'partners.join.title': 'Junte-se à Aventura',
    'partners.join.subtitle': 'Torne-se um parceiro da ASCsurf e participe do desenvolvimento do surfe africano no cenário mundial.',
    'partners.join.meeting': 'Solicitar uma Reunião',
    'partners.stats.partners': 'Parceiros',
    'partners.stats.countries': 'Países Cobertos',
    'partners.stats.audience': 'Audiência',
    'partners.benefits.visibility': 'Visibilidade Internacional',
    'partners.benefits.visibility.desc': 'Exposição em todos os nossos eventos e plataformas digitais em 15 países africanos.',
    'partners.benefits.audience': 'Audiência Engajada',
    'partners.benefits.audience.desc': 'Acesso a uma comunidade apaixonada de mais de 300 atletas e milhares de fãs.',
    'partners.benefits.premium': 'Associação Premium',
    'partners.benefits.premium.desc': 'Alinhamento com valores de excelência, autenticidade e desenvolvimento sustentável.',
    'partners.benefits.custom': 'Parceria Personalizada',
    'partners.benefits.custom.desc': 'Soluções personalizadas de acordo com seus objetivos de marketing e RSE.',
    'partners.levels.title.main': 'Parceiro Título',
    'partners.levels.title.official': 'Parceiro Oficial',
    'partners.levels.title.support': 'Parceiro de Apoio',
    'partners.levels.price.season': 'por temporada',
    'partners.levels.popular': 'Popular',
    'partners.resources.sponsorship': 'Pacote de Patrocínio',
    'partners.resources.sponsorship.desc': 'Documento completo com todas as oportunidades de parceria e tarifas.',
    'partners.resources.media': 'Kit de Mídia',
    'partners.resources.media.desc': 'Logotipos, fotos em alta resolução e elementos gráficos para suas campanhas.',
    'partners.resources.contact': 'Contato Dedicado',
    'partners.resources.contact.desc': 'Interaja diretamente com nossa equipe de parcerias para um projeto personalizado.',
    'partners.resources.download': 'Baixar PDF',
    'partners.resources.access': 'Acessar Kit',
    'partners.resources.write': 'Escreva-nos',
    'partners.contact.us': 'Contate-nos',
    'partners.since': 'Desde',
    'partners.category.title': 'Título',
    'partners.category.equipment': 'Equipamento',
    'partners.category.institutional': 'Institucional',
    'partners.category.official': 'Oficial',
    'partners.category.support': 'Apoio',
    'partners.learnMore': 'Saiba mais',
    'partners.viewProjects': 'Ver projetos',
    
    // Contact
    'contact.hero.title': 'Contate-nos',
    'contact.hero.subtitle': 'Estamos aqui para responder a todas as suas perguntas sobre o surfe africano',
    'contact.form.title': 'Envie-nos uma Mensagem',
    'contact.form.name': 'Nome completo',
    'contact.form.email': 'Email',
    'contact.form.type': 'Tipo de solicitação',
    'contact.form.subject': 'Assunto',
    'contact.form.message': 'Mensagem',
    'contact.form.send': 'Enviar Mensagem',
    'contact.form.placeholder.name': 'Seu nome',
    'contact.form.placeholder.email': 'seu@email.com',
    'contact.form.placeholder.subject': 'Assunto da sua mensagem',
    'contact.form.placeholder.message': 'Descreva sua solicitação em detalhes...',
    'contact.form.types.general': 'Consulta geral',
    'contact.form.types.partnership': 'Parceria',
    'contact.form.types.competition': 'Competições',
    'contact.form.types.media': 'Mídia',
    'contact.form.types.athlete': 'Inscrição de atleta',
    'contact.form.types.federation': 'Adesão de federação',
    'contact.info.title': 'Informações de Contato',
    'contact.info.address': 'Endereço',
    'contact.info.email': 'Email',
    'contact.info.phone': 'Telefone',
    'contact.info.hours': 'Horários',
    'contact.departments.title': 'Contatos por Departamento',
    'contact.departments.subtitle': 'Contate diretamente a pessoa certa',
    'contact.departments.management': 'Gerência Geral',
    'contact.departments.one': 'Equipe de Marketing',
    'contact.departments.two': 'Equipe Técnico-Comercial',
    'contact.departments.competitions': 'Competições',
    'contact.departments.partnerships': 'Parcerias',
    'contact.departments.media': 'Mídia e Comunicação',
    'contact.location.title': 'Nossa Localização',
    'contact.location.subtitle': 'Sede em Dakar, Senegal',
    'contact.location.find': 'Como nos Encontrar',
    'contact.location.address': 'Endereço Completo',
    'contact.location.transport': 'Transporte',
    'contact.location.landmarks': 'Pontos de Referência',
    'contact.location.taxi': 'Táxi: Peça por "Surfer Paradise" Corniche des Almadies',
    'contact.location.repere': 'Logo após a estação de lavagem na Corniche des Almadies',
    'contact.newsletter.title': 'Mantenha-se Conectado',
    'contact.newsletter.subtitle': 'Inscreva-se na nossa newsletter para receber todas as notícias do surfe africano diretamente na sua caixa de entrada.',
    'contact.social.instagram': 'Instagram',
    'contact.social.youtube': 'YouTube',
    'contact.social.facebook': 'Facebook',
    'contact.social.followers': 'seguidores',
    'contact.hours.weekdays': 'Seg - Sex: 9h - 18h',
    'contact.hours.saturday': 'Sáb: 9h - 13h',
    
    // Admin
    'admin.login.title': 'Login de Administração',
    'admin.login.subtitle': 'Acesse o painel de administração da ASCsurf',
    'admin.login.email': 'Email',
    'admin.login.password': 'Senha',
    'admin.login.submit': 'Entrar',
    'admin.login.forgot': 'Esqueceu a senha?',
    'admin.dashboard.title': 'Painel',
    'admin.dashboard.welcome': 'Bem-vindo à administração da ASCsurf',
    'admin.menu.dashboard': 'Painel',
    'admin.menu.news': 'Notícias',
    'admin.menu.athletes': 'Atletas',
    'admin.menu.federations': 'Federações',
    'admin.menu.partners': 'Parceiros',
    'admin.menu.gallery': 'Galeria',
    'admin.menu.sliders': 'Sliders',
    'admin.menu.users': 'Usuários',
    'admin.menu.settings': 'Configurações',
    'admin.menu.logout': 'Sair',
    'admin.stats.news': 'Notícias',
    'admin.stats.athletes': 'Atletas',
    'admin.stats.federations': 'Federações',
    'admin.stats.partners': 'Parceiros',
    'admin.stats.galleries': 'Galerias',
    'admin.stats.sliders': 'Sliders',
    'admin.stats.users': 'Usuários',
    'admin.stats.messages': 'Mensagens',
    'admin.activity.recent': 'Atividade Recente',
    'admin.activity.newArticle': 'Novo artigo publicado',
    'admin.activity.athleteAdded': 'Atleta adicionado',
    'admin.activity.galleryUpdated': 'Galeria atualizada',
    'admin.quickActions.title': 'Ações Rápidas',
    'admin.quickActions.newArticle': 'Novo Artigo',
    'admin.quickActions.newAthlete': 'Novo Atleta',
    'admin.quickActions.newGallery': 'Nova Galeria',
    'admin.quickActions.newSlider': 'Novo Slider',
    'admin.table.search': 'Pesquisar...',
    'admin.table.filter': 'Filtrar',
    'admin.table.actions': 'Ações',
    'admin.table.edit': 'Editar',
    'admin.table.view': 'Ver',
    'admin.table.delete': 'Excluir',
    'admin.table.noResults': 'Nenhum resultado encontrado',
    'admin.modal.add': 'Adicionar',
    'admin.modal.edit': 'Editar',
    'admin.modal.save': 'Salvar',
    'admin.modal.cancel': 'Cancelar',
    'admin.modal.close': 'Fechar',
    'admin.settings.general': 'Configurações gerais',
    'admin.settings.siteName': 'Nome do site',
    'admin.settings.contactEmail': 'Email de contato',
    'admin.settings.defaultLanguage': 'Idioma padrão',
    'admin.settings.save': 'Salvar',
    'admin.news.title': 'Gerenciamento de Notícias',
    'admin.news.add': 'Novo Artigo',
    'admin.athletes.title': 'Gerenciamento de Atletas',
    'admin.athletes.add': 'Novo Atleta',
    'admin.federations.title': 'Gerenciamento de Federações',
    'admin.federations.add': 'Nova Federação',
    'admin.partners.title': 'Gerenciamento de Parceiros',
    'admin.partners.add': 'Novo Parceiro',
    'admin.gallery.title': 'Gerenciamento de Galeria',
    'admin.gallery.add': 'Nova Galeria',
    'admin.sliders.title': 'Gerenciamento de Sliders',
    'admin.sliders.add': 'Novo Slider',
    'admin.users.title': 'Gerenciamento de Usuários',
    'admin.users.add': 'Novo Usuário',
    
    // Footer
    'footer.description': 'Unir nações africanas em torno do surfe, desenvolver talentos locais e promover a África na cena mundial do surfe.',
    'footer.navigation': 'Navegação',
    'footer.contact': 'Contato',
    'footer.copyright': '© 2025 ASCsurf - Confederação Africana de Surfe. Todos os direitos reservados.',
    
    // Common
    'common.readMore': 'Leia mais',
    'common.learnMore': 'Saiba mais',
    'common.seeAll': 'Ver tudo',
    'common.loading': 'Carregando...',
    'common.error': 'Ocorreu um erro',
    'common.success': 'Sucesso',
    'common.email': 'Email',
    'common.phone': 'Telefone',
    'common.address': 'Endereço',
    'common.website': 'Site',
    'common.founded': 'Fundada',
    'common.members': 'Membros',
    'common.participants': 'Participantes',
    'common.competitions': 'Competições',
    'common.victories': 'Vitórias',
    'common.podiums': 'Pódios',
    'common.achievements': 'Conquistas',
    'common.sponsors': 'Patrocinadores',
    'common.country': 'País',
    'common.age': 'Idade',
    'common.specialty': 'Especialidade',
    'common.ranking': 'Classificação',
    'common.send': 'Enviar',
    'common.subscribe': 'Inscrever-se',
    'common.followUs': 'Siga-nos',
    'common.contactUs': 'Contate-nos',
    'common.download': 'Baixar',
    'common.viewProfile': 'Ver perfil',
    'common.upcomingEvents': 'Eventos Futuros',
    'common.featuredNews': 'Em Destaque',
    'common.allNews': 'Todas as Notícias',
    'common.categories': 'Categorias',
    'common.tags': 'Tags',
    'common.author': 'Autor',
    'common.date': 'Data',
    'common.readTime': 'Tempo de leitura',
    'common.location': 'Local',
    'common.prizePool': 'Prêmio Total',
    'common.winners': 'Vencedores',
    'common.highlights': 'Destaques',
    'common.men': 'Homens',
    'common.women': 'Mulheres',
    'common.professional': 'Profissional',
    'common.emerging': 'Emergente',
    'common.close': 'Fechar',
    'common.open': 'Abrir',
    'common.show': 'Mostrar',
    'common.hide': 'Ocultar',
    'common.expand': 'Expandir',
    'common.collapse': 'Recolher',
    'common.previous': 'Anterior',
    'common.next': 'Próximo',
    'common.first': 'Primeiro',
    'common.last': 'Último',
    'common.page': 'Página',
    'common.of': 'de',
    'common.total': 'Total',
    'common.results': 'resultados',
    'common.noResults': 'Nenhum resultado',
    'common.selectAll': 'Selecionar tudo',
    'common.deselectAll': 'Desmarcar tudo',
    'common.selected': 'selecionado(s)',
    'common.required': 'Obrigatório',
    'common.optional': 'Opcional',
    'common.choose': 'Escolher',
    'common.upload': 'Carregar',
    'common.browse': 'Navegar',
    'common.clear': 'Limpar',
    'common.reset': 'Redefinir',
    'common.apply': 'Aplicar',
    'common.submit': 'Enviar',
    'common.continue': 'Continuar',
    'common.back': 'Voltar',
    'common.finish': 'Concluir',
    'common.skip': 'Pular',
    'common.retry': 'Tentar novamente',
    'common.refresh': 'Atualizar',
    'common.reload': 'Recarregar',
    'common.update': 'Atualizar',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.confirm': 'Confirmar',
    'common.yes': 'Sim',
    'common.no': 'Não',
    'common.search': 'Pesquisar',
    'common.filter': 'Filtrar',
    'common.sort': 'Ordenar',
    'common.actions': 'Ações',
    'common.status': 'Status',
    'common.active': 'Ativo',
    'common.inactive': 'Inativo',
    'common.published': 'Publicado',
    'common.draft': 'Rascunho',
    'common.title': 'Título',
    'common.description': 'Descrição',
    'common.content': 'Conteúdo',
    'common.image': 'Imagem',
    'common.video': 'Vídeo',
    'common.gallery': 'Galeria',
    'common.collection': 'Coleção',
    'common.slider': 'Slider',
    'common.slide': 'Slide',
    'common.edit': 'Editar',
    'common.delete': 'Excluir',
    'common.add': 'Adicionar',
    'common.view': 'Ver'
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fr');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['fr']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};